import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { type FormikHelpers, useFormik } from 'formik';
import type React from 'react';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import updateFieldDefMutation from 'src/api/admin/updateFieldDefMutation';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';
import * as Yup from 'yup';
import { decomposeFieldType, isDateFieldType, isMuliValueFieldType, resolveFieldType } from '../../helpers';
import type { AllFieldDefsData, DateDisplayStyleCode, FieldTypeCode, MetadataFieldFormValues } from '../../types';
import AdminFormModal from './AdminFormModal';
import MetadataFieldForm from './MetadataFieldForm';

const UpdateMetadataFieldModal: React.FC = () => {
  const { toastr } = useCustomToast();
  const viewState = useReactiveVar(viewStateVar);

  const closeEditModalHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      editFieldDefModal: false,
    });
  };

  const { data: fieldDefInfo, loading } = useQuery<AllFieldDefsData>(allFieldDefinitionsQuery, {
    fetchPolicy: 'network-only',
    variables: {
      id: viewState.editFieldDefId,
    },
  });

  const rawFieldType = (fieldDefInfo?.allFieldDefs.edges[0]?.node.fieldType as string) || 'TT';
  const { fieldType: decomposedFieldType, datePrecision } = decomposeFieldType(rawFieldType);

  const initialFieldInfo: MetadataFieldFormValues = {
    name: fieldDefInfo?.allFieldDefs.edges[0]?.node.name || '',
    slug: fieldDefInfo?.allFieldDefs.edges[0]?.node.slug || '',
    fieldType: decomposedFieldType as FieldTypeCode,
    datePrecision,
    dateDisplayStyle: (fieldDefInfo?.allFieldDefs.edges[0]?.node.dateDisplayStyle as DateDisplayStyleCode) || 'iso',
    isEditable: fieldDefInfo?.allFieldDefs.edges[0]?.node.isEditable || false,
    highlightIfBlank: fieldDefInfo?.allFieldDefs.edges[0]?.node.highlightIfBlank || false,
    choices: fieldDefInfo?.allFieldDefs.edges[0]?.node.choices || [],
    scopeAssets: fieldDefInfo?.allFieldDefs.edges[0]?.node.scope?.includes('asset') || false,
    scopeGalleries: fieldDefInfo?.allFieldDefs.edges[0]?.node.scope?.includes('collection.gallery') || false,
  };

  const [updateFieldDef] = useMutation(updateFieldDefMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [allFieldDefinitionsQuery],
  });

  const onSuccess = (values: MetadataFieldFormValues, submitProps: FormikHelpers<MetadataFieldFormValues>): void => {
    const { name } = values;
    toastr.success(`Field '${name}' was edited successfully`);
    closeEditModalHandler();
    //reseting the form so formik.dirty is set to false after submitting the form and so the unsaved prompt warning won't show up after saving and leaving the route
    submitProps.resetForm({ values });
  };

  const formik = useFormik<MetadataFieldFormValues>({
    enableReinitialize: true,
    initialValues: {
      ...initialFieldInfo,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please provide a field name'),
    }),
    onSubmit: async (values, submitProps) => {
      const {
        name,
        fieldType,
        datePrecision: dp,
        dateDisplayStyle,
        isEditable,
        highlightIfBlank,
        choices,
        scopeAssets,
        scopeGalleries,
      } = values;

      // Resolve 'DATE' sentinel to the actual backend type code
      const resolvedFieldType = resolveFieldType(fieldType, dp);

      const scope: string[] = [];
      if (scopeAssets) scope.push('asset');
      if (scopeGalleries) scope.push('collection.gallery');

      if (scope.length === 0) {
        toastr.error('Please select at least one Applies To option');
        return;
      }

      const handleSuccess = (): void => {
        onSuccess(values, submitProps);
      };

      const handleError = (e: Error): void => {
        toastr.error(e.message);
      };

      if (isMuliValueFieldType(resolvedFieldType)) {
        updateFieldDef({
          variables: {
            fieldDefId: viewState.editFieldDefId,
            name,
            fieldType: resolvedFieldType,
            choices,
            isEditable,
            highlightIfBlank,
            dateDisplayStyle:
              isDateFieldType(resolvedFieldType) && resolvedFieldType !== 'YO' ? dateDisplayStyle : undefined,
            scope,
          },
        })
          .then(handleSuccess)
          .catch(handleError);
      } else {
        updateFieldDef({
          variables: {
            fieldDefId: viewState.editFieldDefId,
            name,
            fieldType: resolvedFieldType,
            isEditable,
            highlightIfBlank,
            dateDisplayStyle:
              isDateFieldType(resolvedFieldType) && resolvedFieldType !== 'YO' ? dateDisplayStyle : undefined,
            scope,
          },
        })
          .then(handleSuccess)
          .catch(handleError);
      }
    },
  });

  if (loading)
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton animation="wave" variant="text" sx={{ width: '45%', mb: 2, fontSize: '1.5rem' }} />
        {Array.from({ length: 4 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list with fixed length, never reordered
          <Box key={i} sx={{ mb: 2.5 }}>
            <Skeleton animation="wave" variant="text" sx={{ width: '30%', mb: 0.5, fontSize: '0.875rem' }} />
            <Skeleton animation="wave" variant="rounded" sx={{ width: '100%', height: 40 }} />
          </Box>
        ))}
      </Box>
    );

  return (
    <AdminFormModal header="Update Field Definition" formik={formik} onClose={() => closeEditModalHandler()}>
      <MetadataFieldForm formik={formik} />
    </AdminFormModal>
  );
};

export default UpdateMetadataFieldModal;
